import React, { useCallback, useState, useEffect } from "react";
import { inject, observer } from "mobx-react";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import LayoutStore from "library/core/stores/layout/LayoutStore";
import TailwindFlex from "library/components/_tailwind/flex";
import MobileBroadcastPreview from "./mobile-broadcast-preview";
import MobileBroadcastTopBar from "./mobile-broadcast-top-bar";
import MobileBroadcastBottomSheet from "./mobile-broadcast-bottom-sheet";
import MobileBroadcastQuickActions from "./mobile-broadcast-quick-actions";
import MobileBroadcastC2C from "./mobile-broadcast-c2c";
import MobileBroadcastOverlayChat from "./mobile-broadcast-overlay-chat";
import MobileBroadcastPausedCounter from "./mobile-broadcast-paused-counter";
import MobileBroadcastPrivateRequest from "./mobile-broadcast-private-request";
import MobileBroadcastGiftAnimation from "./mobile-broadcast-gift-animation";
import BroadcastVideoSettingsPopover from "../broadcast-popovers/broadcast-settings-popover";
import BroadcastWheelOfFunPopover from "../broadcast-popovers/broadcast-wheel-of-fun-popover";
// import LiveChat8x8 from "common/live-chat/8x8chat";
import { IShowType } from "../_stores/broadcast/enums";

interface MobileBroadcasterProps {
  broadcastStore?: BroadcastStore;
  layoutStore?: LayoutStore;
}

export type BottomSheetTab = "chat" | "showModes" | "viewers" | "support";

const MobileBroadcaster: React.FC<MobileBroadcasterProps> = ({
  broadcastStore,
  layoutStore,
}) => {
  const {
    isVideoSettingsPopoverShown,
    isWheelOfFunSettingsPopoverShown,
    isShowStarted,
    currentShowType,
  } = broadcastStore!;
  const { screenOrientation, isLiteMode } = layoutStore!;

  // Default to showModes if broadcast hasn't started, otherwise chat
  const getDefaultTab = (): BottomSheetTab => {
    return isShowStarted ? "chat" : "showModes";
  };

  const [activeTab, setActiveTab] = useState<BottomSheetTab>(getDefaultTab());
  const [isExpanded, setIsExpanded] = useState(!isShowStarted);

  // Update default tab when show starts/stops
  useEffect(() => {
    if (isShowStarted) {
      setActiveTab("chat");
    } else {
      setActiveTab("showModes");
      setIsExpanded(true);
    }
  }, [isShowStarted]);

  const isLandscape = screenOrientation === "landscape";

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleTabChange = useCallback(
    (tab: BottomSheetTab) => {
      setActiveTab(tab);
      if (!isExpanded) {
        setIsExpanded(true);
      }
    },
    [isExpanded]
  );

  return (
    <TailwindFlex
      position='relative'
      width='w-full'
      flexGrow='flex-grow'
      backgroundColor='bg-black'
      flexDirection='flex-col'
      overflow='overflow-hidden'
      style={{
        // Use dvh (dynamic viewport height) to account for mobile browser UI
        // Fallback to vh for browsers that don't support dvh
        minHeight: isLiteMode ? "100dvh" : "calc(100dvh - 60px)",
        height: isLiteMode ? "100dvh" : "calc(100dvh - 60px)",
      }}>
      {/* Full-screen camera preview */}
      <MobileBroadcastPreview />

      {/* Top bar with status and controls */}
      <MobileBroadcastTopBar />

      {/* Quick action buttons (floating) */}
      <MobileBroadcastQuickActions />

      {/* Cam2Cam viewer (floating, shows when viewer enables c2c) */}
      <MobileBroadcastC2C />

      {/* Gift/tip/buzz animation overlay */}
      <MobileBroadcastGiftAnimation />

      {/* TikTok-style overlay chat (visible when bottom sheet is collapsed) */}
      <MobileBroadcastOverlayChat visible={isShowStarted && !isExpanded} />

      {/* Private show request overlay (accept/decline) */}
      <MobileBroadcastPrivateRequest />

      {/* Paused show countdown overlay (shows after private show ends) */}
      {currentShowType === IShowType.PAUSED && <MobileBroadcastPausedCounter />}

      {/* Bottom sheet with chat and show modes */}
      <MobileBroadcastBottomSheet
        activeTab={activeTab}
        isExpanded={isExpanded}
        isLandscape={isLandscape}
        onTabChange={handleTabChange}
        onToggleExpanded={toggleExpanded}
        onExpand={handleExpand}
        onCollapse={handleCollapse}
      />

      {/* Video settings popover (from cog icon in show modes) */}
      {isVideoSettingsPopoverShown && <BroadcastVideoSettingsPopover />}

      {/* Wheel of fun popover */}
      {isWheelOfFunSettingsPopoverShown && <BroadcastWheelOfFunPopover />}

      {/* Hidden LiveChat8x8 -- initializes 8x8 script and renders hidden button */}
      {/* <div style={{ position: "fixed", left: -9999, top: -9999, opacity: 0 }}>
        <LiveChat8x8 />
      </div> */}
    </TailwindFlex>
  );
};

export default inject(
  "broadcastStore",
  "layoutStore"
)(observer(MobileBroadcaster));
